import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import useIsFlagEnabled from "@/hooks/useIsFlagEnabled";
import { useAppPathname } from "@/routing/navigation";
import { PostHogEvents, useEvents } from "@/tracking/useEvents";
import { DEFAULT_CUSTOMIZE_PATHNAME } from "@/utils/formHref";
import { getFromStorage, setToStorage } from "@/utils/localStorageUtils";
import {
  ABANDONED_CHECKOUT_PROMO_FLAG,
  ABANDONED_CHECKOUT_PROMO_OFFER,
  ABANDONED_CHECKOUT_PROMO_OFFER_ID,
} from "./abandonedCheckoutPromo";
import {
  hasAbandonedCheckout,
  markCheckoutAbandonedIfStarted,
} from "./checkoutAbandonment";
import PromoBanner from "./PromoBanner";
import useAbandonedCheckoutPromoEligibility from "./useAbandonedCheckoutPromoEligibility";

const DISMISSED_STORAGE_KEY = `promoBannerDismissed:${ABANDONED_CHECKOUT_PROMO_OFFER_ID}`;

/**
 * Abandoned-checkout retargeting banner (r-1913), offering 10% off the first
 * order to users without a paid plan who left a started checkout.
 *
 * Mounted only while the promo's release toggle is on (R-2430): switched off,
 * nothing below runs, so a checkout left during the pause is not marked
 * abandoned either.
 */
export default function AbandonedCheckoutPromoBanner() {
  const isPromoEnabled = useIsFlagEnabled(ABANDONED_CHECKOUT_PROMO_FLAG);

  if (!isPromoEnabled) return null;

  return <AbandonedCheckoutPromoOffer />;
}

function AbandonedCheckoutPromoOffer() {
  const navigate = useNavigate();
  const { captureEvent } = useEvents();
  const pathname = useAppPathname();
  const isInCheckoutFlow = pathname.includes(DEFAULT_CUSTOMIZE_PATHNAME);

  const [hasAbandoned, setHasAbandoned] = useState(() =>
    hasAbandonedCheckout()
  );

  // This component stays mounted across dashboard navigation (Drawer), so it
  // is where "the user left checkout and came back" becomes observable: any
  // render outside the checkout flow with a started marker means the checkout
  // was abandoned (see checkoutAbandonment.ts).
  useEffect(() => {
    if (isInCheckoutFlow) return;
    markCheckoutAbandonedIfStarted();
    setHasAbandoned(hasAbandonedCheckout());
  }, [isInCheckoutFlow]);

  const isEligible = useAbandonedCheckoutPromoEligibility(hasAbandoned);

  const [dismissed, setDismissed] = useState(() =>
    Boolean(getFromStorage(DISMISSED_STORAGE_KEY))
  );

  // The customize/checkout flow already carries the offer — the code is
  // auto-applied there (see AbandonedCheckoutPromoAutoApply).
  if (isInCheckoutFlow) return null;

  if (dismissed || !isEligible) return null;

  const { percentOff, code } = ABANDONED_CHECKOUT_PROMO_OFFER;

  return (
    <PromoBanner
      message={
        <>
          {percentOff}% off your first order! Use code: <strong>{code}</strong>
        </>
      }
      ctaLabel="Get Now"
      onCtaClick={() => {
        captureEvent(PostHogEvents.PromoBannerCtaClicked, {
          offer: ABANDONED_CHECKOUT_PROMO_OFFER_ID,
          code,
        });
        void navigate({ to: DEFAULT_CUSTOMIZE_PATHNAME });
      }}
      onDismiss={() => {
        captureEvent(PostHogEvents.PromoBannerDismissed, {
          offer: ABANDONED_CHECKOUT_PROMO_OFFER_ID,
          code,
        });
        setToStorage(DISMISSED_STORAGE_KEY, "true");
        setDismissed(true);
      }}
    />
  );
}
